import { type FaqItem, faqFor } from "@/content/faq";
import { useT } from "@/lib/i18n";

/* Content-blokken als kleine componenten zodat ze `useT()` kunnen gebruiken (reactief bij
 * taalwissel). `body`/`aside` blijven ReactNode; we plaatsen hier <XyzBody /> i.p.v. losse JSX. */
function SafetyBody() {
  const t = useT();
  return (
    <>
      <p>{t("Veiligheidsglas is glas dat bij breuk geen scherpe, gevaarlijke scherven geeft. Bij gehard glas (ESG) valt het uiteen in kleine, stompe korreltjes; bij gelaagd glas (VSG) blijven de scherven aan een taaie folie hangen — dat maakt het ook inbraakvertragend.")}</p>
      <p>{t("Voor glas in en naast deuren, lage borstweringen en beloopbaar glas is veiligheidsglas volgens NEN 3569 vaak verplicht. In de configurator stel uw soort, dikte, maten en bewerkingen in en ziet u direct de prijs.")}</p>
    </>
  );
}
function SafetyAside() {
  const t = useT();
  return (
    <>
      <h2 className="text-lg font-bold text-primary-900">{t("Wanneer veiligheidsglas?")}</h2>
      <ul className="mt-3 space-y-2 text-sm text-primary-600">
        <li>• {t("Glas in en naast deuren")}</li>
        <li>• {t("Borstwering lager dan 85 cm")}</li>
        <li>• {t("Beloopbaar glas (vloer, trap)")}</li>
        <li>• {t("Dakbeglazing (NEN 2608)")}</li>
      </ul>
    </>
  );
}
function ColoredBody() {
  const t = useT();
  return (
    <>
      <p>{t("Gekleurd glas is glas met een gekleurde laklaag of emaillaag aan de achterzijde — een strak, ondoorzichtig paneel in vrijwel elke kleur. Lacobel is glanzend, Matelac mat; geëmailleerd en gehard gelakt glas zijn in nagenoeg elke RAL-kleur leverbaar.")}</p>
      <p>{t("Populair als keukenachterwand, meubel-, wand- of deurpaneel. U stelt maten, boringen en randafwerking in de configurator in en ziet direct de prijs.")}</p>
    </>
  );
}
function ColoredAside() {
  const t = useT();
  return (
    <>
      <h2 className="text-lg font-bold text-primary-900">{t("Veelgebruikte toepassingen")}</h2>
      <ul className="mt-3 space-y-2 text-sm text-primary-600">
        <li>• {t("Keukenachterwand")}</li>
        <li>• {t("Meubel- en kastpaneel")}</li>
        <li>• {t("Wandbekleding")}</li>
        <li>• {t("Deur- en interieurpaneel")}</li>
      </ul>
    </>
  );
}

/**
 * Eén bron voor de vorm van een glas-landingspagina. Stond tot 20-09-2026 twee keer: hier én in
 * CategoryGlasLanding.tsx. Twee kopieën van hetzelfde type lopen stilletjes uit elkaar — een veld
 * dat je aan de ene kant toevoegt bestaat aan de andere kant niet, en dat merk je pas als de
 * compiler klaagt over iets wat er wél staat.
 */
export interface LandingConfig {
  breadcrumb: string;
  h1: string;
  intro: string;
  seo: { title: string; description: string; canonicalPath: string };
  /** Eigen herofoto. Zonder deze valt de pagina terug op de conceptfoto (HERO_CONCEPT). */
  heroImage?: { src: string; widths: number[] };
  subs?: string[];
  comingSoon?: { title: string; desc: string }[];
  comingSoonHeading?: string;
  soortenHeading?: string;
  /** Extra blok bóven de soorten/binnenkort-sectie. */
  extra?: React.ReactNode;
  showExplainer?: boolean;
  siblings: { label: string; to: string }[];
  content: { title: string; body: React.ReactNode; aside?: React.ReactNode };
  faqTitle: string;
  faq: FaqItem[];
}

export const CATEGORY_LANDINGS: Record<string, LandingConfig> = {
  veiligheidsglas: {
    heroImage: { src: "/hero-veiligheidsglas.webp", widths: [640, 1024, 1440, 1672] },
    breadcrumb: "Veiligheidsglas",
    h1: "Veiligheidsglas op maat",
    intro: "Gehard (ESG) en gelaagd (VSG) — sterk, veilig en vaak verplicht bij deuren, lage borstweringen en vloeren. Kies uw soort en stel 'm direct samen.",
    seo: { title: "Veiligheidsglas op maat — gehard & gelaagd", description: "Veiligheidsglas op maat: gehard (ESG), gelaagd (VSG), gehard-gelaagd en beloopbaar glas. Direct de prijs, op maat gemaakt en verzekerd geleverd.", canonicalPath: "/categorie/veiligheidsglas" },
    subs: ["Gehard glas", "Gelaagd glas", "Gehard-gelaagd glas", "Beloopbaar glas"],
    soortenHeading: "Soorten veiligheidsglas",
    siblings: [
      { label: "Enkel glas", to: "/glas-op-maat" },
      { label: "Gekleurd glas", to: "/categorie/gekleurd-glas" },
      { label: "Isolatieglas", to: "/categorie/isolatieglas" },
    ],
    content: {
      title: "Wat is veiligheidsglas?",
      body: <SafetyBody />,
      aside: <SafetyAside />,
    },
    faqTitle: "Veelgestelde vragen over veiligheidsglas",
    faq: faqFor("veiligheidsglas"),
  },
  "gekleurd-glas": {
    heroImage: { src: "/hero-gekleurd-glas.webp", widths: [640, 1024, 1440, 1672] },
    breadcrumb: "Gekleurd glas",
    h1: "Gekleurd glas op maat",
    intro: "Lacobel, Matelac, geëmailleerd en gehard gelakt glas — strakke gekleurde glaspanelen op maat voor keukenwand, meubel en interieur. Kies uw soort en stel 'm direct samen.",
    seo: { title: "Gekleurd glas op maat — Lacobel, Matelac & gelakt", description: "Gekleurd glas op maat: Lacobel, Matelac, geëmailleerd en gehard gelakt glas in vrijwel elke kleur. Direct de prijs, verzekerd geleverd.", canonicalPath: "/categorie/gekleurd-glas" },
    subs: ["Geëmailleerd glas", "Lacobel-Safe", "Lacobel-T", "Matelac-Safe", "Matelac-T", "Gehard gelakt glas"],
    soortenHeading: "Soorten gekleurd glas",
    siblings: [
      { label: "Enkel glas", to: "/glas-op-maat" },
      { label: "Veiligheidsglas", to: "/categorie/veiligheidsglas" },
      { label: "Isolatieglas", to: "/categorie/isolatieglas" },
    ],
    content: {
      title: "Wat is gekleurd glas?",
      body: <ColoredBody />,
      aside: <ColoredAside />,
    },
    faqTitle: "Veelgestelde vragen over gekleurd glas",
    faq: faqFor("gekleurd-glas"),
  },
};
